import { FC } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  capitalizeFirstLetter,
  formatXAxis,
  formatYAxis,
  IAiChart,
  IBiaxialChartConfig,
} from "./chartConfig";
import { customTokens } from "../../theme/theme";
import { useStyles } from "./AiGraphs.styles";

const AiBiaxialChart: FC<IAiChart> = ({ config, chartData }) => {
  const styles = useStyles();

  const {
    data: { x, y1, y2 },
  } = config as IBiaxialChartConfig;

  const xKey = x.field;
  const xLabel = x.label;

  const y1Key = y1.field;
  const y1Label = y1.label;

  const y2Key = y2.field;
  const y2Label = y2.label;

  return (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart
        data={chartData}
        margin={{ left: 40, right: 80 }}
        barGap={0}
        barCategoryGap="0%"
      >
        <CartesianGrid
          stroke={customTokens.colorNeutralBackground4}
          horizontal={true}
          vertical={false}
        />

        <XAxis
          tickFormatter={(v) => formatXAxis(v)}
          dataKey={xKey}
          axisLine={false}
          tickLine={false}
          interval={0}
          label={{
            value: xLabel,
            position: "bottom",
            style: {
              textAnchor: "middle",
              fontWeight: 700,
              fill: customTokens.fontDarkGrayBody,
            },
          }}
        />
        <YAxis
          tickFormatter={(v) => formatYAxis(v)}
          axisLine={false}
          tickLine={false}
          yAxisId="left"
          orientation="left"
          label={{
            value: y1Label,
            angle: -90,
            position: "insideLeft",
            offset: -20,
            style: {
              textAnchor: "middle",
              fontWeight: 700,
              fill: customTokens.fontDarkGrayBody,
            },
          }}
        />
        <YAxis
          tickFormatter={(v) => formatYAxis(v)}
          axisLine={false}
          tickLine={false}
          yAxisId="right"
          orientation="right"
          label={{
            value: y2Label,
            angle: -90,
            position: "right",
            offset: 40,
            style: {
              textAnchor: "middle",
              fontWeight: 700,
              fill: customTokens.fontDarkGrayBody,
            },
          }}
        />

        <Tooltip formatter={(v) => formatYAxis(v)} />

        <Legend
          wrapperStyle={{ paddingTop: 30 }}
          content={({ payload }) => (
            <div className={styles.legendContainer}>
              {payload?.map((entry, index) => (
                <div key={`item-${index}`} className={styles.legendWrapper}>
                  <span
                    className={styles.legendIcon}
                    style={{ backgroundColor: entry.color }}
                  />
                  <span>{capitalizeFirstLetter(entry.value ?? "")}</span>
                </div>
              ))}
            </div>
          )}
        />

        <Bar
          yAxisId="left"
          dataKey={y1Key}
          name={y1Label}
          fill={customTokens.UNFCCCBrandGraphLevel1}
          maxBarSize={40}
        />

        <Bar
          yAxisId="right"
          dataKey={y2Key}
          fill={customTokens.UNFCCCBrandGraphLevel2}
          name={y2Label}
          maxBarSize={40}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default AiBiaxialChart;
